<script setup lang="ts">
import type { LastGame } from '~/types/last-game';

const props = withDefaults(
  defineProps<{
    skipFirst?: boolean;
    alwaysShow?: boolean;
    hideHeader?: boolean;
  }>(),
  {},
);

const { getLastGames, hasGames, onLastGamesUpdate } = useLastGames();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { getResponsiveThumbnailSrc } = useResponsiveThumbnail();
const { showMoneyFormat } = useWallet();
const { isMobile } = useLayout();
const { openPreview } = useGameMobilePreview();
const resolvedNuxtLink = resolveComponent('NuxtLink');

const lastGames = ref<LastGame[]>([]);

const loadLastGames = () => {
  if (isAuthenticated.value && hasGames()) {
    lastGames.value = getLastGames();
  } else {
    lastGames.value = [];
  }
};

let unsubscribe: (() => void) | null = null;

onMounted(() => {
  unsubscribe = onLastGamesUpdate(() => {
    loadLastGames();
  });
});

watch(
  isAuthenticated,
  (newValue) => {
    if (!newValue) {
      lastGames.value = [];
    } else {
      loadLastGames();
    }
  },
  { immediate: true },
);

const shouldShow = computed(() => {
  return isAuthenticated.value && lastGames.value.length > 0;
});

watch(shouldShow, (newValue) => {
  if (newValue) {
    nextTick(() => {
      checkScrollFades();
    });
  }
});

const displayItems = computed(() => {
  const items = props.skipFirst
    ? [...lastGames.value].slice(1)
    : [...lastGames.value];

  return items.slice(0, 10);
});

const formattedPayToday = (game: LastGame): string => {
  if (!game.turnover) return '';

  const value = Number(game.turnover) || 0;

  return showMoneyFormat({ value });
};

const { validateGameUrl } = useGamesWinners();

// No mobile: clicar num card abre o MobilePreview em vez de navegar.
// Mapeia LastGame → shape mínimo de Game pra alimentar useGameMobilePreview.
function handleCardClick(e: MouseEvent, game: LastGame) {
  if (dragged.value) {
    e.preventDefault();

    return;
  }
  if (!isMobile.value) return;

  e.preventDefault();
  openPreview({
    id: String(game.game_id),
    slug: game.slug,
    name: game.game_name,
    image: game.image,
    pay_today: game.pay_today != null ? String(game.pay_today) : undefined,
    turnover: game.turnover != null ? String(game.turnover) : undefined,
    provider: { id: 0, name: game.provider || '' },
    order: null,
    games: undefined,
  } as unknown as Game);
}

const isVideoGame = (game: LastGame): boolean =>
  game.media_type === 'video' || /\.mp4($|\?)/i.test(game.image ?? '');

const videoEls = ref<HTMLVideoElement[]>([]);
const prefersReducedMotion = ref(false);

// Sem poster o card fica preto até o primeiro frame ser decodificado. O campo
// é opcional: enquanto o backend não mandar, fica undefined e quem cobre a
// lacuna é o shimmer (readyVideos).
const videoPosterSrc = (game: LastGame): string | undefined =>
  game.image_poster ? getResponsiveThumbnailSrc(game.image_poster) : undefined;

// Chaveado pela src e não pelo índice: displayItems reordena quando o usuário
// abre um jogo novo, e um índice reaproveitado marcaria como pronto um vídeo
// que ainda não carregou.
const readyVideos = ref(new Set<string>());

// `loadeddata` = primeiro frame disponível. O erro também marca como pronto
// pra não deixar o shimmer preso pra sempre num vídeo que falhou.
const markVideoReady = (src: string) => {
  readyVideos.value.add(src);
};

let videoObserver: IntersectionObserver | null = null;

const setVideoRef = (el: Element | ComponentPublicInstance | null) => {
  if (el instanceof HTMLVideoElement && !videoEls.value.includes(el)) {
    videoEls.value.push(el);
  }
};

const observeVideos = () => {
  if (!videoObserver) return;
  for (const el of videoEls.value) {
    videoObserver.observe(el);
  }
};

const gamesGrid = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);
const dragged = ref(false);
const showFadeLeft = ref(false);
const showFadeRight = ref(false);

const currentScrollLeft = ref(0);
const currentScrollWidth = ref(0);
const currentClientWidth = ref(0);

const updateScrollMetrics = () => {
  if (gamesGrid.value) {
    currentScrollLeft.value = gamesGrid.value.scrollLeft;
    currentScrollWidth.value = gamesGrid.value.scrollWidth;
    currentClientWidth.value = gamesGrid.value.clientWidth;
  }
};

const scrollNext = () => {
  if (gamesGrid.value) {
    const scrollAmount = gamesGrid.value.clientWidth * 0.4;

    gamesGrid.value.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    setTimeout(updateScrollMetrics, 100);
  }
};

const scrollPrev = () => {
  if (gamesGrid.value) {
    const scrollAmount = gamesGrid.value.clientWidth * 0.4;

    gamesGrid.value.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    setTimeout(updateScrollMetrics, 100);
  }
};

const swiperControls = {
  scrollNext,
  scrollPrev,
  currentScrollLeft,
  currentScrollWidth,
  currentClientWidth,
};

const onMouseDown = (e: MouseEvent) => {
  if (!gamesGrid.value) return;
  isDragging.value = true;
  startX.value = e.pageX - gamesGrid.value.offsetLeft;
  scrollLeft.value = gamesGrid.value.scrollLeft;
  dragged.value = false;
};

const onMouseMove = (e: MouseEvent) => {
  if (!isDragging.value || !gamesGrid.value) return;
  e.preventDefault();

  const x = e.pageX - gamesGrid.value.offsetLeft;
  const walk = x - startX.value;

  if (Math.abs(walk) > 5) {
    dragged.value = true;
  }
  gamesGrid.value.scrollLeft = scrollLeft.value - walk;
  updateScrollMetrics();
};

const onMouseUp = () => {
  isDragging.value = false;
  if (dragged.value) {
    setTimeout(() => {
      dragged.value = false;
    }, 50);
  }
};

const onMouseLeave = onMouseUp;

const checkScrollFades = () => {
  if (!gamesGrid.value) return;

  updateScrollMetrics();

  const hasOverflow = gamesGrid.value.scrollWidth > gamesGrid.value.clientWidth;

  showFadeLeft.value = hasOverflow && gamesGrid.value.scrollLeft > 0;
  showFadeRight.value =
    hasOverflow &&
    gamesGrid.value.scrollLeft + gamesGrid.value.clientWidth <
      gamesGrid.value.scrollWidth;
};

onMounted(() => {
  loadLastGames();

  prefersReducedMotion.value =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

  if ('IntersectionObserver' in window) {
    videoObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = entry.target as HTMLVideoElement;

          if (entry.isIntersecting) {
            el.play().catch(() => {
              /* autoplay bloqueado: ignora */
            });
          } else {
            el.pause();
          }
        }
      },
      { threshold: 0.25 },
    );
  }

  nextTick(() => {
    checkScrollFades();
    observeVideos();
  });

  gamesGrid.value?.addEventListener('scroll', checkScrollFades);
  window.addEventListener('resize', checkScrollFades);
});

watch(displayItems, () => {
  videoObserver?.disconnect();
  videoEls.value = [];
  nextTick(() => {
    observeVideos();
  });
});

onBeforeUnmount(() => {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }

  videoObserver?.disconnect();
  videoObserver = null;
  videoEls.value = [];

  if (gamesGrid.value) {
    gamesGrid.value.removeEventListener('scroll', checkScrollFades);
  }
  window.removeEventListener('resize', checkScrollFades);
});

defineExpose({
  swiperControls,
});
</script>

<template>
  <div
    v-if="shouldShow"
    class="last-games-played"
  >
    <div
      class="last-games-played__container"
      :class="{ 'last-games-played__container--no-bg': hideHeader }"
    >
      <!-- Tab header com título -->
      <div
        v-if="!hideHeader"
        class="last-games-played__header"
      >
        <div class="last-games-played__title-tab">
          <span class="last-games-played__title-text"
            >JOGOS
            <span class="last-games-played__title-highlight"
              >RECENTES</span
            ></span
          >
        </div>
      </div>

      <!-- Grid de cards horizontal -->
      <div
        class="last-games-played__body"
        :class="{ 'last-games-played__body--no-bg': hideHeader }"
      >
        <div
          ref="gamesGrid"
          class="last-games-played__grid"
          :class="{ 'last-games-played__grid--dragging': dragged }"
          @mousedown="onMouseDown"
          @mouseleave="onMouseLeave"
          @mousemove="onMouseMove"
          @mouseup="onMouseUp"
        >
          <template
            v-for="(game, index) in displayItems"
            :key="index"
          >
            <component
              :is="isMobile ? 'div' : resolvedNuxtLink"
              class="last-games-played__card"
              draggable="false"
              :role="isMobile ? 'button' : undefined"
              :tabindex="isMobile ? 0 : undefined"
              :to="!isMobile ? validateGameUrl(game.slug) : undefined"
              @click="(e: MouseEvent) => handleCardClick(e, game)"
            >
              <!-- Imagem do jogo (lado esquerdo) -->
              <div class="last-games-played__image-wrapper">
                <template v-if="game.image && game.image !== '0'">
                  <video
                    v-if="isVideoGame(game)"
                    :ref="setVideoRef"
                    v-autoplay-muted
                    :aria-label="game.game_name"
                    :autoplay="!prefersReducedMotion"
                    class="last-games-played__image"
                    disablepictureinpicture
                    :loop="!prefersReducedMotion"
                    muted
                    playsinline
                    :poster="videoPosterSrc(game)"
                    preload="metadata"
                    :src="game.image"
                    @error="markVideoReady(game.image)"
                    @loadeddata="markVideoReady(game.image)"
                  />
                  <img
                    v-else
                    :alt="game.game_name"
                    class="last-games-played__image"
                    draggable="false"
                    loading="lazy"
                    :src="getResponsiveThumbnailSrc(game.image)"
                  />
                  <!-- Cobre o fundo escuro do wrapper até o vídeo ter frame.
                       Só quando não há poster — com poster, o próprio <video>
                       já pinta algo. -->
                  <div
                    v-if="
                      isVideoGame(game) &&
                      !videoPosterSrc(game) &&
                      !readyVideos.has(game.image)
                    "
                    class="last-games-played__image-placeholder skeleton-loading"
                  />
                </template>
                <template v-else>
                  <div class="last-games-played__image-fallback">
                    <span>{{ game.game_name }}</span>
                  </div>
                </template>
              </div>

              <!-- Conteúdo do card (lado direito) -->
              <div class="last-games-played__content">
                <div class="last-games-played__name">
                  {{ game.game_name }}
                </div>

                <div class="last-games-played__provider">Apostado Hoje</div>
                <div
                  v-if="formattedPayToday(game)"
                  class="last-games-played__pay-today mb-0.5"
                >
                  {{ formattedPayToday(game) }}
                </div>

                <button class="last-games-played__play-button">
                  JOGAR AGORA
                </button>
              </div>
            </component>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.last-games-played {
  width: 100%;
  user-select: none;
  -webkit-user-select: none;
  padding-bottom: 16px;
}

/* Container com formato de tab + corpo */
.last-games-played__container {
  position: relative;
}

/* Header com tab */
.last-games-played__header {
  display: flex;
}

.last-games-played__title-tab {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #000098;
  border-radius: 6px 6px 0 0;
  line-height: 22px;
  position: relative;
}

.last-games-played__title-tab::after {
  content: '';
  position: absolute;
  left: 100%;
  bottom: 0;
  width: 6px;
  height: 6px;
  background: radial-gradient(circle at 100% 0, transparent 6px, #000098 6px);
}

.last-games-played__title-text {
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.last-games-played__title-highlight {
  font-size: 13px;
  font-weight: 700;
  color: #00c9ff;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

/* Corpo principal */
.last-games-played__body {
  background: #000098;
  border-radius: 0 6px 6px 6px;
  padding: 10px;
}

.last-games-played__body--no-bg {
  background: none;
  border-radius: 0;
  padding: 0;
}

/* Grid de cards */
.last-games-played__grid {
  display: flex;
  flex-direction: row;
  gap: 20px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
}

.last-games-played__grid--dragging {
  cursor: grabbing;
}

.last-games-played__grid::-webkit-scrollbar {
  display: none;
}

/* Card - layout horizontal */
.last-games-played__card {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 12px;
  flex-shrink: 0;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
}

.last-games-played__grid--dragging .last-games-played__card {
  pointer-events: none;
}

/* Imagem do jogo (lado esquerdo) - width fixo, height estica com o content */
.last-games-played__image-wrapper {
  width: 72px;
  overflow: hidden;
  border-radius: 4px;
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.2);
  /* Referência de posicionamento do __image-placeholder */
  position: relative;
}

.last-games-played__image-placeholder {
  position: absolute;
  inset: 0;
  border-radius: 4px;
}

.last-games-played__card:hover .last-games-played__image-wrapper {
  opacity: 0.85;
}

.last-games-played__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.last-games-played__image-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 4px;
  text-align: center;
  background: rgba(255, 255, 255, 0.05);
}

.last-games-played__image-fallback span {
  color: #ffffff;
  font-size: 9px;
  font-weight: 700;
  line-height: 1.2;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Conteudo do card (lado direito) - largura baseada no valor em R$ */
.last-games-played__content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: max-content;
  min-width: 0;
  height: 100%;
}

/* Nome do jogo */
.last-games-played__name {
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.3;
  font-family: 'Plus Jakarta Sans', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 11ch;
}

/* Pagou Hoje label */
.last-games-played__provider {
  font-size: 11px;
  font-weight: 700;
  color: #00c9ff;
  font-family: 'Plus Jakarta Sans', sans-serif;
  white-space: nowrap;
}

/* Valor turnover */
.last-games-played__pay-today {
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  font-family: 'Plus Jakarta Sans', sans-serif;
  white-space: nowrap;
}

/* Botao JOGAR AGORA */
.last-games-played__play-button {
  background: #ffffff;
  color: #0808b3;
  border: none;
  border-radius: 5px;
  padding: 5px 12px;
  font-size: 11px;
  font-weight: 800;
  font-family: 'Plus Jakarta Sans', sans-serif;
  cursor: pointer;
  transition: opacity 0.2s ease;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  white-space: nowrap;
}

.last-games-played__play-button:hover {
  background: #e0e0e0;
}

/* Mobile - tamanhos base do Figma, gap 14px */
@media (max-width: 768px) {
  .last-games-played__grid {
    gap: 14px;
  }

  .last-games-played__card {
    gap: 10px;
  }

  .last-games-played__image-wrapper {
    width: 63px;
  }

  .last-games-played__content {
    width: max-content;
  }

  .last-games-played__name {
    font-size: 12px;
    max-width: 11ch;
  }

  .last-games-played__provider {
    font-size: 10px;
  }

  .last-games-played__pay-today {
    font-size: 12px;
  }

  .last-games-played__play-button {
    font-size: 10px;
    padding: 4px 10px;
  }
}

@media (max-width: 414px) {
  .last-games-played__body {
    padding: 8px;
  }

  .last-games-played__title-tab {
    padding: 3px 8px;
  }

  .last-games-played__title-text,
  .last-games-played__title-highlight {
    font-size: 11px;
  }
}
</style>
